import React, { useState, useEffect, useMemo } from 'react';
import { useForm, useFieldArray, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Checkbox, Autocomplete, AutocompleteItem, Divider, addToast } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import { Save, X, Languages, BookOpen, Info, ChevronDown, ChevronUp } from 'lucide-react';
import { FormInput, FormTextarea, FormMultiCategorySelector } from '../../base';
import { courseFormSchema, CourseFormData } from './schemas';
import GuidesReorderList from './GuidesReorderList';
import { Guide } from '@/services/apis/guides';
import { useLanguage } from '@/contexts/LanguageContext';

interface CourseFormProps {
  defaultValues?: Partial<CourseFormData>;
  courseId?: string | null;
  onFormSubmit: SubmitHandler<CourseFormData>;
  guideSearchQuery: string;
  setGuideSearchQuery: (query: string) => void;
  availableGuides: Guide[];
  isLoadingGuides: boolean;
  isSubmitting: boolean;
  isLoadingCourse: boolean;
  onCancel?: () => void;
}

const CourseForm: React.FC<CourseFormProps> = ({
  defaultValues,
  onFormSubmit,
  guideSearchQuery,
  setGuideSearchQuery,
  availableGuides,
  isLoadingGuides,
  isSubmitting,
  isLoadingCourse,
  onCancel,
}) => {
  const { t } = useTranslation('admin');
  const { currentLanguage } = useLanguage();
  const [showSpanishTranslation, setShowSpanishTranslation] = useState(
    !!(defaultValues?.spanishName || defaultValues?.spanishDescription),
  );

  const { control, handleSubmit, watch, reset, setValue } = useForm({
    resolver: zodResolver(courseFormSchema),
    mode: 'onChange',
    defaultValues: {
      name: '',
      description: '',
      spanishName: '',
      spanishDescription: '',
      categoryIds: [],
      isRecommended: false,
      guides: [],
      ...defaultValues,
    },
  });

  const {
    fields: guideFields,
    append: appendGuide,
    remove: removeGuide,
    replace: replaceGuides,
  } = useFieldArray({
    control,
    name: 'guides',
  });

  const guides = watch('guides') || [];

  useEffect(() => {
    if (defaultValues) {
      reset(defaultValues);
      if (defaultValues.spanishName || defaultValues.spanishDescription) {
        setShowSpanishTranslation(true);
      }
    }
  }, [defaultValues, reset]);

  const filteredAvailableGuides = useMemo(() => {
    return availableGuides.filter((guide) => !guides.some((g) => g.guideId === guide.id));
  }, [availableGuides, guides]);

  const handleSelectGuide = (guide: Guide) => {
    if (guides.some((g) => g.guideId === guide.id)) {
      addToast({
        title: t('courses.form.guides.alreadyAdded'),
        color: 'warning',
      });
      return;
    }

    // Get guide title from translations
    const guideTranslation = guide.translations?.find((t) => t.language === currentLanguage) || guide.translations?.[0];
    const guideTitle = guideTranslation?.title || 'Untitled Guide';

    const newGuide = {
      guideId: guide.id,
      title: guideTitle,
      isPublished: guide.isPublished || false,
      position: guides.length + 1,
    };

    appendGuide(newGuide);
  };

  const handleRemoveGuide = (guideId: string) => {
    const index = guides.findIndex((g) => g.guideId === guideId);
    if (index === -1) return;
    removeGuide(index);
  };

  const handleReorderGuides = (reorderedGuides: typeof guides) => {
    if (!reorderedGuides) return;
    // Update positions based on new order
    const updatedGuides = reorderedGuides.map((g, index) => ({
      ...g,
      position: index + 1,
    }));
    replaceGuides(updatedGuides);
  };

  const handleAutocompleteSelection = (key: React.Key | null) => {
    if (!key) return;
    const guideId = key as string;
    const selectedGuide = filteredAvailableGuides.find((g) => g.id === guideId);
    if (selectedGuide) {
      handleSelectGuide(selectedGuide);
      setGuideSearchQuery('');
    }
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
      {/* Basic Information Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-primary-600 dark:text-primary-400" />
          <h3 className="font-semibold text-lg">{t('courses.form.tabs.basic')}</h3>
        </div>
        <Divider />
        <FormInput
          name="name"
          control={control}
          label={t('courses.form.name.label')}
          placeholder={t('courses.form.name.placeholder')}
          isRequired
          variant="bordered"
          isDisabled={isSubmitting || isLoadingCourse}
        />

        <FormTextarea
          name="description"
          control={control}
          label={t('courses.form.description.label')}
          placeholder={t('courses.form.description.placeholder')}
          rows={4}
          variant="bordered"
          isDisabled={isSubmitting || isLoadingCourse}
        />

        <FormMultiCategorySelector name="categoryIds" control={control} label={t('courses.form.categories.label')} />

        <Checkbox
          isSelected={watch('isRecommended')}
          onValueChange={(value) => setValue('isRecommended', value)}
          isDisabled={isSubmitting || isLoadingCourse}
          classNames={{
            label: 'text-sm',
          }}
        >
          {t('courses.form.isRecommended.label')}
        </Checkbox>
      </div>

      {/* Spanish Translation Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Languages className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            <h3 className="font-semibold text-lg">{t('courses.form.tabs.translation')}</h3>
          </div>
          <Button
            variant="flat"
            size="sm"
            onPress={() => setShowSpanishTranslation(!showSpanishTranslation)}
            endContent={
              showSpanishTranslation ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
            }
            isDisabled={isSubmitting || isLoadingCourse}
          >
            {showSpanishTranslation ? t('courses.form.translation.hide') : t('courses.form.translation.show')}
          </Button>
        </div>
        <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-blue-800 dark:text-blue-200">
            {t('courses.form.translation.info', {
              defaultValue:
                'Provide Spanish translations for the course name and description. This will make the course available to Spanish-speaking users.',
            })}
          </p>
        </div>
        {showSpanishTranslation && (
          <>
            <Divider />

            <FormInput
              name="spanishName"
              control={control}
              label={t('courses.form.spanishName.label')}
              placeholder={t('courses.form.spanishName.placeholder')}
              variant="bordered"
              isDisabled={isSubmitting || isLoadingCourse}
            />

            <FormTextarea
              name="spanishDescription"
              control={control}
              label={t('courses.form.spanishDescription.label')}
              placeholder={t('courses.form.spanishDescription.placeholder')}
              rows={4}
              variant="bordered"
              isDisabled={isSubmitting || isLoadingCourse}
            />
          </>
        )}
      </div>

      {/* Guides Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            <h3 className="font-semibold text-lg">{t('courses.form.tabs.guides')}</h3>
            {guides.length > 0 && (
              <span className="px-2 py-0.5 text-xs font-medium bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300 rounded-full">
                {guides.length}
              </span>
            )}
          </div>
        </div>
        <Divider />
        <div className="space-y-4">
          <Autocomplete
            label={t('courses.form.guides.addGuide')}
            placeholder={t('courses.form.guides.searchPlaceholder')}
            variant="bordered"
            inputValue={guideSearchQuery}
            onInputChange={setGuideSearchQuery}
            onSelectionChange={handleAutocompleteSelection}
            isLoading={isLoadingGuides}
            isDisabled={isSubmitting || isLoadingCourse}
            allowsCustomValue={false}
            selectedKey={null}
          >
            {filteredAvailableGuides.map((guide) => {
              const guideTranslation =
                guide.translations?.find((t) => t.language === currentLanguage) || guide.translations?.[0];
              const guideTitle = guideTranslation?.title || 'Untitled Guide';
              return (
                <AutocompleteItem key={guide.id} textValue={guideTitle}>
                  <div className="flex flex-col">
                    <span className="font-medium">{guideTitle}</span>
                    {guideTranslation?.description && (
                      <span className="text-xs text-gray-500 line-clamp-1">{guideTranslation.description}</span>
                    )}
                  </div>
                </AutocompleteItem>
              );
            })}
          </Autocomplete>

          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('courses.form.guides.description', {
              defaultValue: 'Add and organize guides for this course. You can reorder them by dragging.',
            })}
          </p>

          <GuidesReorderList guides={guideFields} onReorder={handleReorderGuides} onRemove={handleRemoveGuide} />
        </div>
      </div>

      {/* Form Actions */}
      <Divider />
      <div className="flex justify-end gap-3">
        {onCancel && (
          <Button variant="flat" onPress={onCancel} startContent={<X className="w-4 h-4" />} isDisabled={isSubmitting}>
            {t('common.cancel')}
          </Button>
        )}
        <Button
          type="submit"
          color="primary"
          isLoading={isSubmitting}
          startContent={!isSubmitting ? <Save className="w-4 h-4" /> : undefined}
          isDisabled={isSubmitting || isLoadingCourse}
        >
          {t('common.save')}
        </Button>
      </div>
    </form>
  );
};

export default CourseForm;
